import * as React from 'react';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { ApplicationState } from '../../../store';
import { alertErrors, alertSuccess, alertWarn } from '../../../store/reducers/Alert';
import { BreadCrumbItem, PageBreadCrumb } from '../../../components/shared/PageBreadCrumb';
import {
    assignIdTreeIcons,
    buildTreeFromProjectDocFolders,
    markGeneralSectionNodes
} from '../../../helpers/tree/ProjectDocFolder';
import { constantSearchParams } from '../../../helpers/constants';
import { DocTemplateFolderTypeVals } from '../../../helpers/constants/modelValues/DocTemplateFolderTypeVals';
import { findNode } from '../../../helpers/tree';
import { FolderFiles } from './folder/FolderFiles';
import { PrevIterationFolderFiles } from './folder/PrevIterationFolderFiles';
import { getProjectDocStatusBadge } from '../../../helpers/mapping/ProjectDocStatusVals';
import { getProjectStatusBadge } from '../../../helpers/mapping/ProjectStatus';
import { KAProjectDocFolder, KAProjectDocFolderRef } from './folder/KAProjectDocFolder';
import { AddActDialog } from './folder/AddActDialog';
import { AddActDocumentDialog } from './folder/AddActDocumentDialog';
import { AddActDocumentModel, AddActModel } from '../services/ProjectDocFolderService';
import { Notes } from '../../tasks/components/Notes';
import { ProjectDoc } from '../models/ProjectDoc';
import { ProjectDocFolder } from '../models/ProjectDocFolder';
import { ProjectDocFolderTreeNode } from '../models/ProjectDocFolderTreeNode';
import { services } from '../../../services';
import { useBackgroundPromiseEffectByCondition } from '../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { useBackgroundPromiseWrapper } from '../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDataStorage } from '../../../helpers/hooks/useDataStorage';
import { useURLSearchParam } from '../../../helpers/hooks/useURLSearchParam';
import { FolderApproversListDefault } from './folder/FolderApproversListDefault';
import { Act } from '../models/Act';
import { ActEdit } from './act/ActEdit';
import { ActPrevIteration } from './act/ActPrevIteration';
import { ProjectDocTypeVals } from '../../../helpers/mapping/ProjectDocTypes';
import { normalizeProjectType, ProjectTypeValues } from '../../../helpers/mapping/ProjectTypes';
import { ContractorProjectDocActions } from '../../../helpers/constants/modelActions/ContractorProjectDocActions';
import { ModelAction } from '../../../types/responses/ModelAction';
import { PDFAttachmentTypeVals } from '../../../helpers/mapping/PDFAttachmentTypeVals';
import { ProjectDocFlowType } from '../models/enums/ProjectDocFlowType';
import { useUnsavedChanges } from '../../../helpers/hooks/useUnsavedChanges';
import { Card } from '../../../components/synced/ui/Card/Card';
import { TabStrip } from '../../../components/synced/ui/TabStrip/TabStrip';
import { NewButton } from '../../../components/synced/ui/NewButton/NewButton';
import { EmptyState } from '../../../components/synced/ui/EmptyState/EmptyState';
import { useIsMobile } from '../../../helpers/hooks/useIsMobile';
import { useSignerForm } from './folder/useSignerForm';
import { SignatoryList } from './folder/SignatoryList';
import { SignatoryEditor } from './folder/SignatoryEditor';
import { KADocHistoryDefault } from '../../history/components/KADocHistoryDefault';
import { PaperSigningTabPanel, PaperSigningTabPanelRef } from './folder/PaperSigningTabPanel';
import { ProjectDocSignerRoleCode } from '../models/enums/ProjectDocSignerRoleCode';
import { ProjectDocFolderStatuses } from '../../../helpers/mapping/ProjectDocFolderStatuses';
import { DocumentTypeVals } from '../../../helpers/mapping/DocumentTypeVals';
import { useNoteUpdates } from '../../notes/hooks/useNoteUpdates';
import { useDocumentationNotesPaging } from '../hooks/useDocumentationNotesPaging';
import {
    ACT_CONTAINER_TITLES,
    FIRE_SAFETY_FOLDER_TITLES,
    ROOT_SYSTEM_FOLDER_TITLES,
    SUBSECTION_DOC_FOLDER_TITLES
} from '../../../helpers/constants/systemValues/SectionSystemFolderTitles';

type LeftTab = 'structure' | 'paperSigning' | 'signers' | 'history';
type MobileTab = 'info' | 'paperSigning' | 'signers' | 'history';
const VISITED_KEY = 'pd_signers_visited';

export const ProjectDocEdit: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const idKey = +(id ?? '0');
    const [projectDoc, setProjectDoc] = React.useState<ProjectDoc>();
    const isExecutiveReconstruction =
        projectDoc?.Id === idKey &&
        projectDoc?.DocTypeId === ProjectDocTypeVals.Executive &&
        normalizeProjectType(projectDoc?.Project?.Directorate) === ProjectTypeValues.Reconstruction;
    const [refreshPD, setRefreshPD] = React.useState<boolean>(false);
    const [leftTab, setLeftTab] = React.useState<LeftTab>('structure');
    const [mobileTab, setMobileTab] = React.useState<MobileTab>('info');
    const [sectionsOpen, setSectionsOpen] = React.useState(false);
    const [selectedSignerIndex, setSelectedSignerIndex] = React.useState<number>(0);
    const [signerDirty, setSignerDirty] = React.useState<boolean>(false);
    const [selectedPaperSigningSlot, setSelectedPaperSigningSlot] = React.useState<ProjectDocFolder | undefined>();
    const [paperSigningGenerated, setPaperSigningGenerated] = React.useState(false);

    useBackgroundPromiseEffectByCondition(
        () => idKey > 0,
        () => services.projectDocService.getById(idKey),
        (res: ProjectDoc) => setProjectDoc(res),
        [idKey, refreshPD]
    );

    const [projectActions, setProjectActions] = React.useState<ModelAction[]>([]);
    useBackgroundPromiseEffectByCondition(
        () => idKey > 0,
        () => services.projectDocService.actions(idKey),
        (res: ModelAction[]) => setProjectActions(res),
        [idKey, refreshPD]
    );
    const signerReadonly = !projectActions.some((a) => a.Id === ContractorProjectDocActions.EditSigner);

    const refreshProjectDoc = () => setRefreshPD((x) => !x);
    const search = useURLSearchParam();
    const notesPaging = useDocumentationNotesPaging();
    const tabIndex = +(search.searchParams.get(constantSearchParams.Tab) ?? 0);
    const setTabIndex = (index: number) => search.onUpdate(constantSearchParams.Tab, index);
    const onUpdateAppovers = () => onCurrentFolderStatusUpdated();

    const breadCrumbItems: BreadCrumbItem[] = [
        { label: `Документация`, command: () => navigate(`/projectdocs`), url: '/projectdocs' },
        { label: projectDoc?.Title, status: { ...projectDoc?.Status, mapBadgeClass: getProjectDocStatusBadge } }
    ];

    const isFirstSignerVisit = (projectDocId: number) => {
        if (!projectDocId) return false;
        try {
            const raw = localStorage.getItem(VISITED_KEY);
            const visited: number[] = raw ? JSON.parse(raw) : [];
            return !visited.includes(projectDocId);
        } catch {
            return false;
        }
    };

    const markSignerVisited = (id: number) => {
        try {
            const raw = localStorage.getItem(VISITED_KEY);
            const visited: number[] = raw ? JSON.parse(raw) : [];
            if (!visited.includes(id)) {
                visited.push(id);
                localStorage.setItem(VISITED_KEY, JSON.stringify(visited));
            }
        } catch {}
    };

    const dictionaries = useSelector((appState: ApplicationState) => appState.dictionariesContext.dictionariesContext);

    const filteredFolders = React.useMemo(() => {
        const folders = projectDoc?.Folders ?? [];
        const paperSigningRootIds = new Set(
            folders.filter((f) => f.FolderTypeId === DocTemplateFolderTypeVals.PaperSigningRoot).map((f) => f.Id)
        );
        return folders.filter(
            (f) =>
                f.FolderTypeId !== DocTemplateFolderTypeVals.PaperSigningRoot &&
                (f.ParentId == null || !paperSigningRootIds.has(f.ParentId))
        );
    }, [projectDoc]);

    const treeNodes = React.useMemo(() => {
        const tree = buildTreeFromProjectDocFolders(filteredFolders, dictionaries.ProjectDocFolderStatuses, null);
        markGeneralSectionNodes(tree);
        if (projectDoc?.DocTypeId === ProjectDocTypeVals.Executive) {
            assignIdTreeIcons(tree);
        }
        return tree;
    }, [filteredFolders, dictionaries]);

    React.useEffect(() => {
        if (!projectDoc || projectDoc.Id !== idKey) return;
        const urlSelectedNode = findNode(treeNodes, +(search.searchParams.get(constantSearchParams.Node) ?? 0));
        const folderTypeId = urlSelectedNode?.FolderTypeId;
        const hasNotes =
            folderTypeId === DocTemplateFolderTypeVals.Act ||
            ((folderTypeId === DocTemplateFolderTypeVals.Tom ||
                folderTypeId === DocTemplateFolderTypeVals.ActDocument) &&
                (urlSelectedNode?.data?.DocumentTypeId === DocumentTypeVals.ResponsiblePersonOrder ||
                    urlSelectedNode?.data?.StatusId !== ProjectDocFolderStatuses.OrderDocument));
        const restoreNotes = hasNotes && notesPaging.hasSavedPaging && [2, 3, 4].includes(tabIndex);
        if (isExecutiveReconstruction && isFirstSignerVisit(idKey) && !restoreNotes) {
            markSignerVisited(idKey);
            setLeftTab('signers');
            setMobileTab('signers');
        }
    }, [projectDoc]);

    const folderIds = React.useMemo(() => {
        const collect = (nodes: ProjectDocFolderTreeNode[]): number[] =>
            nodes.flatMap((n) => [n.key as number, ...collect(n.children as ProjectDocFolderTreeNode[])]);
        return collect(treeNodes);
    }, [treeNodes]);

    const [selectedNode, setSelectedNode] = React.useState<ProjectDocFolderTreeNode | undefined>(
        () => findNode(treeNodes, +(search.searchParams.get(constantSearchParams.Node) ?? 0)) ?? undefined
    );
    React.useEffect(() => {
        setSelectedNode(findNode(treeNodes, +(search.searchParams.get(constantSearchParams.Node) ?? 0)) ?? undefined);
    }, [treeNodes, search.searchParams]);

    const onSelectNode = (node) => {
        setSelectedNode(node);
        setSelectedPaperSigningSlot(undefined);
        notesPaging.selectFolder(node.key);
    };

    const [promise] = useBackgroundPromiseWrapper();

    const onCurrentFolderStatusUpdated = () => {
        const projectDocFolderId: number = selectedNode?.data?.Id ?? 0;
        promise(
            () => services.projectDocFolderService.getById(projectDocFolderId),
            (response: ProjectDocFolder) => {
                if (response && projectDoc) {
                    projectDoc.Folders?.splice(
                        projectDoc.Folders.findIndex((x) => x.Id === response.Id),
                        1,
                        response
                    );
                    setProjectDoc({ ...projectDoc });
                }
            }
        );
    };

    const [dataStorage, setDataStorageValue, clearDataStorage] = useDataStorage([selectedNode?.data?.Id ?? 0]);
    const noteUpdates = useNoteUpdates(selectedNode?.data?.Id ?? 0);
    React.useEffect(
        () =>
            noteUpdates.subscribe(() => {
                for (const type of ['Recived', 'Completed', 'Archived']) {
                    dataStorage.set(`Notes_${type}_NotesPager_taskNotes`, undefined);
                }
            }),
        [noteUpdates, dataStorage]
    );
    const dispatch = useDispatch();

    const [addActDialogVisible, setAddActDialogVisible] = React.useState(false);
    const [addActDocumentDialogVisible, setAddActDocumentDialogVisible] = React.useState(false);
    const [folderForAddAct, setFolderForAddAct] = React.useState<ProjectDocFolder | undefined>();
    const [folderForAddActDocument, setFolderForAddActDocument] = React.useState<ProjectDocFolder | undefined>();

    const onAddAct = (data?: AddActModel) => {
        if (!data || !idKey) return;
        promise(
            () =>
                services.projectDocSignerService.validateForActTemplate({
                    projectDocId: idKey,
                    actTemplateId: data.ActTemplateId
                }),
            (errors: string[]) => {
                if (errors.length === 0) {
                    doAddAct(data);
                } else {
                    dispatch(alertErrors(errors.map((e) => ({ summary: 'Ошибка подписантов', detail: e }))));
                    setAddActDialogVisible(false);
                    setFolderForAddAct(undefined);
                    const roleByError: [string, ProjectDocSignerRoleCode][] = [
                        ['Генподрядчик', ProjectDocSignerRoleCode.Contractor],
                        ['Проектировщик', ProjectDocSignerRoleCode.Designer],
                        ['СК генподрядчика', ProjectDocSignerRoleCode.SkContractor],
                        ['Исполнитель работ', ProjectDocSignerRoleCode.ActParty],
                        ['Иное лицо', ProjectDocSignerRoleCode.Other]
                    ];
                    const firstErrorRole = roleByError.find(([label]) => errors.some((e) => e.startsWith(label)));
                    if (firstErrorRole) {
                        const roleCode = firstErrorRole[1];
                        const idx = signerApi.signers.findIndex((s) => s.RoleId === roleCode);
                        if (idx >= 0) setSelectedSignerIndex(idx);
                    }
                    switchLeftTab('signers');
                    switchMobileTab('signers');
                }
            }
        );
    };

    const doAddAct = (data: AddActModel) => {
        promise(
            () => services.projectDocFolderService.addAct(data),
            () => {
                dispatch(alertSuccess());
                refreshProjectDoc();
                setAddActDialogVisible(false);
                setFolderForAddAct(undefined);
            }
        );
    };

    const onAddActDocument = (data?: AddActDocumentModel) => {
        if (!data) return;
        promise(
            () => services.projectDocFolderService.addActDocument(data),
            () => {
                dispatch(alertSuccess());
                refreshProjectDoc();
                setAddActDocumentDialogVisible(false);
                setFolderForAddActDocument(undefined);
            }
        );
    };

    const canShowAddAct = isExecutiveReconstruction && !paperSigningGenerated;

    const [availableActTemplatesCount, setAvailableActTemplatesCount] = React.useState<number | undefined>(undefined);

    const selectedFolderForActCheck = React.useMemo(() => {
        const folderTypeId = selectedNode?.FolderTypeId;
        if (
            folderTypeId !== DocTemplateFolderTypeVals.Section &&
            folderTypeId !== DocTemplateFolderTypeVals.ActSection
        ) {
            return undefined;
        }
        if (selectedNode?.data?.ParentId == null) return undefined;
        const title = selectedNode?.data?.Title ?? '';
        if (ROOT_SYSTEM_FOLDER_TITLES.includes(title) && !ACT_CONTAINER_TITLES.includes(title)) {
            return undefined;
        }
        if (SUBSECTION_DOC_FOLDER_TITLES.includes(title)) {
            return undefined;
        }
        return selectedNode?.data?.Id;
    }, [selectedNode]);

    useBackgroundPromiseEffectByCondition(
        () => Boolean(selectedFolderForActCheck),
        () => services.projectDocFolderService.getAvailableActTemplates(selectedFolderForActCheck!),
        (res) => setAvailableActTemplatesCount(res?.length ?? 0),
        [selectedFolderForActCheck]
    );

    React.useEffect(() => {
        if (!selectedFolderForActCheck) {
            setAvailableActTemplatesCount(undefined);
        }
    }, [selectedFolderForActCheck]);

    const canShowAddActButton = (availableActTemplatesCount ?? 0) > 0;

    const kaFolderRef = React.useRef<KAProjectDocFolderRef>(null);
    const paperSigningTabPanelRef = React.useRef<PaperSigningTabPanelRef>(null);

    const signerApi = useSignerForm({
        projectDocId: idKey,
        contractorId: projectDoc?.Id === idKey ? projectDoc.ContractorId : undefined,
        readonly: signerReadonly,
        projectDocFlowType: projectDoc?.DocFlowType ?? ProjectDocFlowType.Electronic,
        onSaved: refreshProjectDoc,
        onDirtyChange: setSignerDirty
    });
    const { requestNavigate } = useUnsavedChanges(signerDirty);

    const isMobile = useIsMobile(900);

    const isSignersTabActive =
        isExecutiveReconstruction && (isMobile ? mobileTab === 'signers' : leftTab === 'signers');

    // Приказ, загруженный через том, обновляет BasisDocumentId на бэке перечитываем подписантов при входе на вкладку
    React.useEffect(() => {
        if (isSignersTabActive && !signerDirty) signerApi.reload();
    }, [isSignersTabActive, signerDirty, signerApi.reload]);

    React.useEffect(() => {
        if (!isExecutiveReconstruction) {
            setLeftTab((tab) => (tab === 'signers' ? 'structure' : tab));
            setMobileTab((tab) => (tab === 'signers' ? 'info' : tab));
        }
    }, [isExecutiveReconstruction]);

    const switchLeftTab = (tab: LeftTab) => {
        if (tab === leftTab) return;
        const doSwitch = () => {
            setLeftTab((prev) => {
                if (prev === 'paperSigning') setSelectedPaperSigningSlot(undefined);
                return tab;
            });
            if (tab === 'paperSigning') setSelectedNode(undefined);
            if (tab === 'structure') clearDataStorage();
        };
        if (leftTab === 'signers') {
            requestNavigate(() => {
                if (signerDirty) signerApi.reload();
                doSwitch();
            });
        } else {
            doSwitch();
        }
    };

    const switchMobileTab = (tab: MobileTab) => {
        if (tab === mobileTab) return;
        const doSwitch = () => {
            setMobileTab((prev) => {
                if (prev === 'paperSigning') setSelectedPaperSigningSlot(undefined);
                return tab;
            });
            if (tab === 'paperSigning') setSelectedNode(undefined);
            if (tab === 'info') clearDataStorage();
        };
        if (mobileTab === 'signers') {
            requestNavigate(() => {
                if (signerDirty) signerApi.reload();
                doSwitch();
            });
        } else {
            doSwitch();
        }
    };

    const onActDeleted = React.useCallback(() => {
        setSelectedNode(undefined);
        search.onUpdate(constantSearchParams.Node, undefined);
        refreshProjectDoc();
    }, [refreshProjectDoc, search]);

    const onDocumentDeleted = React.useCallback(() => {
        setSelectedNode(undefined);
        search.onUpdate(constantSearchParams.Node, undefined);
        refreshProjectDoc();
    }, [refreshProjectDoc, search]);

    React.useEffect(() => {
        if (!paperSigningGenerated) {
            setLeftTab((t) => (t === 'paperSigning' ? 'structure' : t));
            setMobileTab((t) => (t === 'paperSigning' ? 'info' : t));
        }
    }, [paperSigningGenerated]);

    const notesTab = (type: 'Recived' | 'Completed' | 'Archived') => {
        if (
            projectDoc?.Id !== idKey ||
            selectedNode?.data?.Id !== +(search.searchParams.get(constantSearchParams.Node) ?? 0)
        )
            return null;
        return (
            <Notes
                key={`${selectedNode?.data?.Id ?? 0}:${type}`}
                type={type}
                projectDocFolderId={selectedNode?.data?.Id ?? 0}
                projectDocFolderStatusId={selectedNode?.data?.StatusId ?? 0}
                projectDoc={projectDoc}
                dataStorage={dataStorage}
                setDataStorageValue={setDataStorageValue}
                onNoteChanged={clearDataStorage}
                noteUpdates={noteUpdates}
                paging={notesPaging.getPaging(type)}
            />
        );
    };

    const notifTab = () => (
        <FolderApproversListDefault
            projectDocFolderId={selectedNode?.data?.Id ?? 0}
            projectDocFolder={selectedNode?.data as ProjectDocFolder}
            approvers={selectedNode?.data?.ProjectDocFolderCUsers ?? []}
            onUpdateAppovers={onUpdateAppovers}
        />
    );

    const renderTabbed = (labels: string[], contents: React.ReactNode[], tabKeys?: number[]) => {
        const keys = tabKeys ?? labels.map((_, index) => index);
        const requestedKey = Number.isInteger(tabIndex) ? tabIndex : 0;
        const activeIndex = tabKeys
            ? Math.max(0, keys.indexOf(requestedKey))
            : Math.min(Math.max(requestedKey, 0), labels.length - 1);
        const activeKey = keys[activeIndex];
        return (
            <React.Fragment>
                <TabStrip
                    scrollable
                    tabs={labels.map((l, i) => ({ key: String(keys[i]), label: l }))}
                    activeKey={String(activeKey)}
                    onChange={(k) => setTabIndex(+k)}
                />
                <div className="pd-tab-content" key={activeKey}>
                    {contents[activeIndex]}
                </div>
            </React.Fragment>
        );
    };

    const renderStructureRight = () => {
        const folderTypeId = selectedNode?.FolderTypeId;

        if (selectedPaperSigningSlot) {
            return renderTabbed(
                ['Файлы'],
                [
                    <React.Fragment>
                        <div className="p-3">
                            <p className="font-weight-bold mb-1">PDF для подписания:</p>
                            <FolderFiles
                                projectDocFolderId={selectedPaperSigningSlot.Id!}
                                readonly={true}
                                typeIdFilter={PDFAttachmentTypeVals.PaperSigningGenerated}
                                fetchFiles={(folderId) =>
                                    services.paperSigningService.getSlotAttachments(
                                        folderId,
                                        PDFAttachmentTypeVals.PaperSigningGenerated
                                    )
                                }
                                onCurrentFolderStatusUpdated={() => {}}
                                hideFolderActions={true}
                                dataStorage={dataStorage}
                                setDataStorageValue={setDataStorageValue}
                                onDeleted={onDocumentDeleted}
                                folderCypher={selectedNode?.data?.Cypher}
                                folderDocumentOrganization={selectedNode?.data?.DocumentOrganization}
                            />
                            <p className="font-weight-bold mb-1 mt-3">Подписанный файл:</p>
                            <FolderFiles
                                projectDocFolderId={selectedPaperSigningSlot.Id!}
                                uploadTypeId={PDFAttachmentTypeVals.PaperSigningSigned}
                                typeIdFilter={PDFAttachmentTypeVals.PaperSigningSigned}
                                hideFolderActions={true}
                                fetchFiles={(folderId) =>
                                    services.paperSigningService.getSlotAttachments(
                                        folderId,
                                        PDFAttachmentTypeVals.PaperSigningSigned
                                    )
                                }
                                maxFiles={1}
                                dataStorage={dataStorage}
                                setDataStorageValue={setDataStorageValue}
                                onCurrentFolderStatusUpdated={() => {
                                    kaFolderRef.current?.loadActions();
                                    paperSigningTabPanelRef.current?.loadActions();
                                }}
                                onDeleted={onDocumentDeleted}
                                folderCypher={selectedNode?.data?.Cypher}
                                folderDocumentOrganization={selectedNode?.data?.DocumentOrganization}
                            />
                        </div>
                    </React.Fragment>
                ]
            );
        }

        if (folderTypeId === DocTemplateFolderTypeVals.Tom || folderTypeId === DocTemplateFolderTypeVals.ActDocument) {
            const isResponsiblePersonOrder =
                selectedNode?.data?.DocumentTypeId === DocumentTypeVals.ResponsiblePersonOrder;
            const isOrder =
                isResponsiblePersonOrder || selectedNode?.data?.StatusId === ProjectDocFolderStatuses.OrderDocument;
            if (isResponsiblePersonOrder) {
                return renderTabbed(
                    ['Файлы', 'Полученные замечания', 'Выполненные замечания', 'Архив замечаний'],
                    [
                        <FolderFiles
                            projectDocFolderId={selectedNode?.data?.Id ?? 0}
                            docTypeId={projectDoc?.DocTypeId}
                            folderTypeId={selectedNode?.FolderTypeId}
                            contractorName={projectDoc?.ContractSignerFullName}
                            onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdated}
                            dataStorage={dataStorage}
                            setDataStorageValue={setDataStorageValue}
                            projectDocFolderStatusId={selectedNode?.data?.StatusId ?? 0}
                            noteUpdates={noteUpdates}
                            projectDoc={projectDoc}
                            fetchFiles={(folderId) =>
                                services.pDFAttachmentsService.fetch({
                                    ProjectDocFolderId: folderId,
                                    IsDeleted: false
                                })
                            }
                            accept=".pdf"
                            onInvalidFiles={() =>
                                dispatch(alertWarn({ detail: 'Допускается загрузка только PDF файлов' }))
                            }
                            onDeleted={onDocumentDeleted}
                            folderCypher={selectedNode?.data?.Cypher}
                            folderDocumentOrganization={selectedNode?.data?.DocumentOrganization}
                        />,
                        notesTab('Recived'),
                        notesTab('Completed'),
                        notesTab('Archived')
                    ],
                    [0, 2, 3, 4]
                );
            }
            if (isOrder) {
                return renderTabbed(
                    ['Файлы'],
                    [
                        <React.Fragment>
                            <FolderFiles
                                projectDocFolderId={selectedNode?.data?.Id ?? 0}
                                docTypeId={projectDoc?.DocTypeId}
                                folderTypeId={selectedNode?.FolderTypeId}
                                contractorName={projectDoc?.ContractSignerFullName}
                                onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdated}
                                dataStorage={dataStorage}
                                setDataStorageValue={setDataStorageValue}
                                projectDocFolderStatusId={selectedNode?.data?.StatusId ?? 0}
                                noteUpdates={noteUpdates}
                                projectDoc={projectDoc}
                                onDeleted={onDocumentDeleted}
                                folderCypher={selectedNode?.data?.Cypher}
                                folderDocumentOrganization={selectedNode?.data?.DocumentOrganization}
                            />
                        </React.Fragment>
                    ]
                );
            }
            return renderTabbed(
                [
                    'Файлы',
                    'Предыдущее согласование',
                    'Полученные замечания',
                    'Выполненные замечания',
                    'Архив замечаний',
                    'Архив файлов',
                    'Уведомления'
                ],
                [
                    <React.Fragment>
                        <FolderFiles
                            projectDocFolderId={selectedNode?.data?.Id ?? 0}
                            docTypeId={projectDoc?.DocTypeId}
                            folderTypeId={selectedNode?.FolderTypeId}
                            contractorName={projectDoc?.ContractSignerFullName}
                            onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdated}
                            dataStorage={dataStorage}
                            setDataStorageValue={setDataStorageValue}
                            projectDocFolderStatusId={selectedNode?.data?.StatusId ?? 0}
                            noteUpdates={noteUpdates}
                            projectDoc={projectDoc}
                            onDeleted={onDocumentDeleted}
                            folderCypher={selectedNode?.data?.Cypher}
                            folderDocumentOrganization={selectedNode?.data?.DocumentOrganization}
                        />
                        {folderTypeId === DocTemplateFolderTypeVals.ActDocument && isExecutiveReconstruction && (
                            <div className="pd-info-banner">
                                Для вида проекта "Реконструкция" допускается загрузка не более одного файла
                            </div>
                        )}
                    </React.Fragment>,
                    <PrevIterationFolderFiles
                        projectDocFolderId={selectedNode?.data?.Id ?? 0}
                        onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdated}
                        dataStorage={dataStorage}
                        setDataStorageValue={setDataStorageValue}
                        projectDocFolderStatusId={selectedNode?.data?.StatusId ?? 0}
                        projectDoc={projectDoc}
                        fetchFiles={(id) => services.pDFAttachmentsService.previousIterationFetch(id)}
                        noteUpdates={noteUpdates}
                        emptyText="Нет файлов предыдущего согласования"
                        storageKey="prevIteration"
                    />,
                    notesTab('Recived'),
                    notesTab('Completed'),
                    notesTab('Archived'),
                    <PrevIterationFolderFiles
                        projectDocFolderId={selectedNode?.data?.Id ?? 0}
                        onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdated}
                        dataStorage={dataStorage}
                        setDataStorageValue={setDataStorageValue}
                        projectDocFolderStatusId={selectedNode?.data?.StatusId ?? 0}
                        projectDoc={projectDoc}
                        emptyText="Нет файлов предыдущих итераций"
                        noteUpdates={noteUpdates}
                        storageKey="archive"
                    />,
                    notifTab()
                ]
            );
        }

        if (folderTypeId === DocTemplateFolderTypeVals.Act) {
            return renderTabbed(
                [
                    'Форма акта',
                    'Акт предыдущей итерации',
                    'Полученные замечания',
                    'Выполненные замечания',
                    'Архив замечаний',
                    'Уведомления'
                ],
                [
                    selectedNode?.data?.Id ? (
                        <ActEdit
                            docFolderId={selectedNode.data.Id}
                            dataStorage={dataStorage}
                            onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdated}
                            onDeleted={onActDeleted}
                            setDataStorageValue={setDataStorageValue}
                        />
                    ) : null,
                    selectedNode?.data?.Id ? (
                        <ActPrevIteration docFolderId={selectedNode.data.Id} dataStorage={dataStorage} />
                    ) : null,
                    notesTab('Recived'),
                    notesTab('Completed'),
                    notesTab('Archived'),
                    notifTab()
                ]
            );
        }

        if (
            folderTypeId === DocTemplateFolderTypeVals.Section &&
            (FIRE_SAFETY_FOLDER_TITLES.includes(selectedNode?.data?.Title ?? '') ||
                SUBSECTION_DOC_FOLDER_TITLES.includes(selectedNode?.data?.Title ?? '')) &&
            canShowAddAct
        ) {
            return renderTabbed(
                ['Документы'],
                [
                    <div className="pd-settings">
                        <NewButton
                            variant="primary"
                            icon="fa-solid fa-file"
                            onClick={() => {
                                if (selectedNode?.data) {
                                    setFolderForAddActDocument(selectedNode.data);
                                    setAddActDocumentDialogVisible(true);
                                }
                            }}
                        >
                            Добавить документ
                        </NewButton>
                    </div>
                ]
            );
        }

        if (
            (folderTypeId === DocTemplateFolderTypeVals.Section ||
                folderTypeId === DocTemplateFolderTypeVals.ActSection ||
                folderTypeId === DocTemplateFolderTypeVals.ActDocumentSection) &&
            canShowAddAct
        ) {
            return renderTabbed(
                ['Настройки'],
                [
                    <div className="pd-settings">
                        {(folderTypeId === DocTemplateFolderTypeVals.Section ||
                            folderTypeId === DocTemplateFolderTypeVals.ActSection) &&
                            canShowAddAct &&
                            canShowAddActButton &&
                            (() => {
                                const title = selectedNode?.data?.Title ?? '';
                                const isNonActSystemFolder =
                                    ROOT_SYSTEM_FOLDER_TITLES.includes(title) && !ACT_CONTAINER_TITLES.includes(title);
                                return !isNonActSystemFolder;
                            })() && (
                                <NewButton
                                    variant="primary"
                                    icon="fa-solid fa-file"
                                    onClick={() => {
                                        if (selectedNode?.data) {
                                            setFolderForAddAct(selectedNode.data);
                                            setAddActDialogVisible(true);
                                        }
                                    }}
                                >
                                    Добавить акт
                                </NewButton>
                            )}
                        {folderTypeId === DocTemplateFolderTypeVals.ActDocumentSection && canShowAddAct && (
                            <NewButton
                                variant="primary"
                                icon="fa-solid fa-file"
                                onClick={() => {
                                    if (selectedNode?.data) {
                                        setFolderForAddActDocument(selectedNode.data);
                                        setAddActDocumentDialogVisible(true);
                                    }
                                }}
                            >
                                Добавить документ
                            </NewButton>
                        )}
                    </div>
                ]
            );
        }

        return (
            <EmptyState
                icon="fa-folder-open"
                title="Выберите раздел"
                text={`Выберите раздел или файл в структуре ${isMobile ? 'выше' : 'слева'}, чтобы просмотреть его содержимое.`}
            />
        );
    };

    if (isMobile)
        return (
            <React.Fragment>
                <PageBreadCrumb items={breadCrumbItems} grayAncestors />

                <div className="pd-mobile-tabs">
                    {(
                        [
                            ['info', 'Информация'],
                            ...(paperSigningGenerated ? [['paperSigning', 'Подписание']] : []),
                            ...(isExecutiveReconstruction ? [['signers', 'Подписанты']] : []),
                            ['history', 'История']
                        ] as [MobileTab, string][]
                    ).map(([key, label]) => (
                        <button
                            key={key}
                            className={clsx('pd-mobile-tab', mobileTab === key && 'pd-mobile-tab--active')}
                            onClick={() => switchMobileTab(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="pd-mobile-body">
                    {mobileTab === 'info' && (
                        <>
                            <Card className="pd-sections-card">
                                <button className="pd-sections-toggle" onClick={() => setSectionsOpen((o) => !o)}>
                                    <i className="fa-solid fa-folder-tree pd-sections-icon" />
                                    <span className="pd-sections-toggle-left">
                                        <span className="pd-sections-header-row">
                                            <span
                                                className={clsx(
                                                    'pd-sections-label',
                                                    !selectedNode && 'pd-sections-label--primary'
                                                )}
                                            >
                                                Разделы
                                            </span>
                                            <i
                                                className="fa-solid fa-chevron-right"
                                                style={{ fontSize: 7, color: '#aeaeb2' }}
                                            />
                                        </span>
                                        {selectedNode && (
                                            <span className="pd-sections-node">
                                                {selectedNode.Cypher && (
                                                    <span className="pd-sections-cypher">{selectedNode.Cypher}</span>
                                                )}
                                                <span className="pd-sections-name">{selectedNode.label}</span>
                                            </span>
                                        )}
                                    </span>
                                    <span className="pd-sections-toggle-right">
                                        {selectedNode?.data?.CompletionPercentage != null &&
                                            (() => {
                                                const pct = selectedNode.data.CompletionPercentage;
                                                const barColor =
                                                    pct === 0 ? '#ff3b30' : pct >= 100 ? '#34c759' : '#ff9f0a';
                                                const trackBg = pct === 0 ? '#fdebeb' : '#eef0f2';
                                                const pctColor =
                                                    pct === 0 ? '#d23b32' : pct >= 100 ? '#1c8a4d' : '#b06a07';
                                                return (
                                                    <span className="pd-sections-pct">
                                                        <span
                                                            className="pd-sections-bar"
                                                            style={{ background: trackBg }}
                                                        >
                                                            <span style={{ width: `${pct}%`, background: barColor }} />
                                                        </span>
                                                        <span
                                                            className="pd-sections-pct-label"
                                                            style={{ color: pctColor }}
                                                        >
                                                            {pct}%
                                                        </span>
                                                    </span>
                                                );
                                            })()}
                                        <i
                                            className={clsx(
                                                'fa-solid fa-chevron-down pd-sections-chevron',
                                                sectionsOpen && 'pd-sections-chevron--open'
                                            )}
                                        />
                                    </span>
                                </button>
                                {sectionsOpen && (
                                    <div className="pd-sections-tree">
                                        <KAProjectDocFolder
                                            treeNodes={treeNodes}
                                            projectDocId={projectDoc?.Id ?? 0}
                                            projectDoc={projectDoc}
                                            docTypeId={projectDoc?.DocTypeId ?? 0}
                                            onSelectNode={(node) => {
                                                onSelectNode(node);
                                                setSectionsOpen(false);
                                            }}
                                            selectedNode={selectedNode}
                                            refreshData={refreshProjectDoc}
                                            ref={kaFolderRef}
                                            onPaperSigningGeneratedChange={setPaperSigningGenerated}
                                        />
                                    </div>
                                )}
                            </Card>
                            <Card className="pd-right">{renderStructureRight()}</Card>
                        </>
                    )}

                    {mobileTab === 'paperSigning' && (
                        <>
                            <Card className="pd-left">
                                <div className="pd-left-body">
                                    <PaperSigningTabPanel
                                        ref={paperSigningTabPanelRef}
                                        projectDocId={idKey}
                                        onProjectDocStatusUpdated={refreshProjectDoc}
                                        selectedSlotId={selectedPaperSigningSlot?.Id}
                                        onSelectSlot={(slot) => {
                                            setSelectedPaperSigningSlot(slot);
                                            setSelectedNode(undefined);
                                        }}
                                    />
                                </div>
                            </Card>
                            <Card className="pd-right">{renderStructureRight()}</Card>
                        </>
                    )}

                    {mobileTab === 'signers' && (
                        <>
                            <Card className="pd-left">
                                <div className="pd-left-body">
                                    <SignatoryList
                                        api={signerApi}
                                        selectedIndex={selectedSignerIndex}
                                        onSelect={setSelectedSignerIndex}
                                        contractorTasksDueDate={projectDoc?.ContractorTasksDueDate}
                                        contractorSignDueDate={projectDoc?.ContractorSignDueDate}
                                    />
                                </div>
                            </Card>
                            <Card className="pd-right">
                                <SignatoryEditor api={signerApi} index={selectedSignerIndex} />
                            </Card>
                        </>
                    )}

                    {mobileTab === 'history' && (
                        <Card className="pd-right">
                            <KADocHistoryDefault projectDocId={projectDoc?.Id ?? 0} folderIds={folderIds} />
                        </Card>
                    )}
                </div>

                {folderForAddAct && (
                    <AddActDialog
                        visible={addActDialogVisible}
                        folder={folderForAddAct}
                        docTypeId={projectDoc?.DocTypeId ?? 0}
                        onSave={onAddAct}
                        onCancel={() => {
                            setAddActDialogVisible(false);
                            setFolderForAddAct(undefined);
                        }}
                    />
                )}
                {folderForAddActDocument && (
                    <AddActDocumentDialog
                        visible={addActDocumentDialogVisible}
                        folder={folderForAddActDocument}
                        docTypeId={projectDoc?.DocTypeId ?? 0}
                        onSave={onAddActDocument}
                        onCancel={() => {
                            setAddActDocumentDialogVisible(false);
                            setFolderForAddActDocument(undefined);
                        }}
                    />
                )}
            </React.Fragment>
        );

    return (
        <React.Fragment>
            <PageBreadCrumb items={breadCrumbItems} grayAncestors />
            <div className="pd-grid">
                <Card className="pd-left">
                    <TabStrip
                        tabs={[
                            { key: 'structure', label: 'Структура' },
                            ...(paperSigningGenerated
                                ? [{ key: 'paperSigning', label: 'Структура на подписание' }]
                                : []),
                            { key: 'history', label: 'История' },
                            ...(isExecutiveReconstruction ? [{ key: 'signers', label: 'Подписанты' }] : [])
                        ]}
                        activeKey={leftTab}
                        onChange={(k) => switchLeftTab(k as LeftTab)}
                    />
                    <div className="pd-left-body">
                        {leftTab === 'structure' ? (
                            <KAProjectDocFolder
                                treeNodes={treeNodes}
                                projectDocId={projectDoc?.Id ?? 0}
                                projectDoc={projectDoc}
                                docTypeId={projectDoc?.DocTypeId ?? 0}
                                onSelectNode={onSelectNode}
                                selectedNode={selectedNode}
                                refreshData={refreshProjectDoc}
                                ref={kaFolderRef}
                                onPaperSigningGeneratedChange={setPaperSigningGenerated}
                            />
                        ) : leftTab === 'paperSigning' ? (
                            <PaperSigningTabPanel
                                ref={paperSigningTabPanelRef}
                                projectDocId={idKey}
                                onProjectDocStatusUpdated={refreshProjectDoc}
                                selectedSlotId={selectedPaperSigningSlot?.Id}
                                onSelectSlot={(slot) => {
                                    setSelectedPaperSigningSlot(slot);
                                    setSelectedNode(undefined);
                                }}
                            />
                        ) : leftTab === 'signers' ? (
                            <SignatoryList
                                api={signerApi}
                                selectedIndex={selectedSignerIndex}
                                onSelect={setSelectedSignerIndex}
                                contractorTasksDueDate={projectDoc?.ContractorTasksDueDate}
                                contractorSignDueDate={projectDoc?.ContractorSignDueDate}
                            />
                        ) : (
                            <EmptyState icon="fa-clock-rotate-left" title="История" />
                        )}
                    </div>
                </Card>

                <Card className="pd-right">
                    {leftTab === 'signers' ? (
                        <SignatoryEditor api={signerApi} index={selectedSignerIndex} />
                    ) : leftTab === 'history' ? (
                        <KADocHistoryDefault projectDocId={projectDoc?.Id ?? 0} folderIds={folderIds} />
                    ) : (
                        renderStructureRight()
                    )}
                </Card>
            </div>

            {folderForAddAct && (
                <AddActDialog
                    visible={addActDialogVisible}
                    folder={folderForAddAct}
                    docTypeId={projectDoc?.DocTypeId ?? 0}
                    onSave={onAddAct}
                    onCancel={() => {
                        setAddActDialogVisible(false);
                        setFolderForAddAct(undefined);
                    }}
                />
            )}
            {folderForAddActDocument && (
                <AddActDocumentDialog
                    visible={addActDocumentDialogVisible}
                    folder={folderForAddActDocument}
                    docTypeId={projectDoc?.DocTypeId ?? 0}
                    onSave={onAddActDocument}
                    onCancel={() => {
                        setAddActDocumentDialogVisible(false);
                        setFolderForAddActDocument(undefined);
                    }}
                />
            )}
        </React.Fragment>
    );
};
